/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI
 * Copyright IBM Corp. 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define([
	'lodash',
	'react',
	'prop-types',
	'ba-ui-carbon',
	'moment-timezone',
	'baglass/core-client/js/core-client/utils/ClassFactory',
	'../../common/Constants',
	'./controls/SingleLineToggle',
	'./controls/SingleLineLink',
	'./controls/SectionTitle',
	'./controls/Separator',
	'../../nls/StringResources'
], (_, React, PropTypes, Carbon, MomentTimezone, ClassFactory, Constants, SingleLineToggle, SingleLineLink, SectionTitle, Separator, StringResources) => {

	const toggleStrings = { disabled: '', enabled: '' };

	const baseTextDirections = [{ id: 'RTL', label: StringResources.get('rtl') }, { id: 'LTR', label: StringResources.get('ltr') },{ id: 'AUTO', label: StringResources.get('contextual') }];

	const DEFAULT_TIMEZONE = 'America/New_York';

	const { Dropdown, Loading } = Carbon;

	const makeItemsList = (items) => {
		return Object.keys(items).map(id => ({ id, label: items[id] }));
	};

	const itemToElement = (item) => {
		return (<div data-value={item.id} title={item.label}>{item.label}</div>);
	};

	const translateWithId = (id) => {
		switch(id){
			case 'open.menu':
				return StringResources.get('openMenu');
			case 'close.menu':
				return StringResources.get('closeMenu');
			default:
				return id;
		}
	};

	return class SettingsTab extends React.Component {

		static propTypes = {
			Glass: PropTypes.any,
			slideout: PropTypes.any
		}

		constructor() {
			super(...arguments);
			this.state = {
				loading: true
			};
		}

		componentDidMount() {
			const configSvc = this.props.Glass.getCoreSvc('.Config');
			const userProfileSvc = this.props.Glass.getCoreSvc('.UserProfile');
			// Make sure product / content locales and time zones are loaded before render.
			return Promise.all([
				this._isSetHomeEnabled(),
				this.props.Glass.getHomePageObject(),
				configSvc.getProductLocales(),
				configSvc.getContentLocales(),
				configSvc.getTimeZones(),
				userProfileSvc.getCapabilities()
			]).then(values => {

				const [ isSetHomeEnabled ] = values;
				const { preferences, capabilities } = userProfileSvc;

				let isHomePageDefault = false;
				try {
					const homePage = preferences.homePage;
					const homeObj = homePage && JSON.parse(homePage);
					isHomePageDefault = homeObj ? homeObj.perspective === 'home' : true;
				} catch ( err ) {
					void(err);
				}

				let timeZoneID = preferences.timeZoneID;
				const timeZoneObj = configSvc[configSvc.TIMEZONES];
				if (timeZoneObj && (MomentTimezone.tz.zone(preferences.timeZoneID) === null || !timeZoneObj.hasOwnProperty(preferences.timeZoneID))) {
					timeZoneID = DEFAULT_TIMEZONE;
				}

				const showWelcomeBanner = this._getShowWelcomeBanner();
				const canUseShowHiddenObjects = capabilities && (capabilities.indexOf('canUseShowHiddenObjectsPreference') >= 0);
				this.setState({
					loading: false,
					showWelcomeBanner,
					isSetHomeEnabled,
					isHomePageDefault,
					accessibilityFeatures: preferences.accessibilityFeatures,
					biDirectionalFeaturesEnabled: preferences.biDirectionalFeaturesEnabled,
					showHiddenObjects: preferences.showHiddenObjects,
					canUseShowHiddenObjects,
					format: preferences.format,
					productLocale: preferences.productLocale,
					contentLocale: preferences.contentLocale,
					timeZoneID: timeZoneID,
					baseTextDirection: preferences.baseTextDirection
				});
			});
		}

		_getItems() {
			const items = [];
			if (!this.state.loading) {
				const configSvc = this.props.Glass.getCoreSvc('.Config');

				items.push(<SectionTitle
					name="general"
					label={StringResources.get('general')}
				/>);

				// Set as home  --> Reset to Default || Default
				items.push(<SingleLineLink
					name="homePage"
					label={StringResources.get('homePage')}
					value={this.state.isHomePageDefault ? StringResources.get('defaultHome') : StringResources.get('resetHome')}
					enabled={!this.state.isHomePageDefault}
					clickCallback={this._setHomeToDefault.bind(this)}
				/>);

				if ( this.state.isSetHomeEnabled ) {
					items.push(<SingleLineLink
						name="homePage"
						enabled={true}
						label={StringResources.get('setHomeLabel')}
						value={StringResources.get('setHomeButton')}
						clickCallback={this._setAsHome.bind(this)}
					/>);
				}

				// Current --> display if not on home?

				items.push(<Separator name="generalSeparator"/>);


				items.push(<SingleLineToggle
					name="welcomeBanner"
					label={StringResources.get('welcomeBanner')}
					enabled={this.state.showWelcomeBanner}
					toggleStrings={toggleStrings}
					onToggleChange={this._toggleWelcomeBanner.bind(this)}
				/>);

				// Getting Started -> Toggle 	???

				items.push(<SingleLineToggle
					name="enableAccessibilityFeatures"
					label={StringResources.get('accessibilityFeatures')}
					enabled={this.state.accessibilityFeatures}
					toggleStrings={toggleStrings}
					onToggleChange={this._changeTogglePreference.bind(this, 'accessibilityFeatures')}
				/>);

				items.push(<Separator name="contentSeparator"/>);

				items.push(<SectionTitle
					name="contentSettings"
					label={StringResources.get('contentSettings')}
				/>);

				if (this.state.canUseShowHiddenObjects) {
					items.push(<SingleLineToggle
						name="showHiddenObjects"
						label={StringResources.get('showHiddenObjects')}
						enabled={this.state.showHiddenObjects}
						toggleStrings={toggleStrings}
						onToggleChange={this._changeTogglePreference.bind(this, 'showHiddenObjects')}
					/>);
				}

				// Report format -> DropDown
				items.push(<Dropdown
					ariaLabel={StringResources.get('format')}
					id="format"
					items={this._getReportFormats()}
					itemToElement={itemToElement}
					label={this._getReportFormat(this.state.format)}
					selected={this.state.format}
					titleText={StringResources.get('format')}
					onChange={this._changeReportFormat.bind(this)}
					translateWithId={(stringId) => translateWithId(stringId)}
				/>);

				items.push(<Separator name="regionalSeparator"/>);

				items.push(<SectionTitle
					name="regionalOptions"
					label={StringResources.get('regionalOptions')}
				/>);

				items.push(<Dropdown
					ariaLabel={StringResources.get('timeZoneID')}
					id="timeZoneID"
					selected={this.state.timeZoneID}
					items={makeItemsList(configSvc[configSvc.TIMEZONES])}
					itemToElement={itemToElement}
					label={configSvc[configSvc.TIMEZONES][this.state.timeZoneID]}
					titleText={StringResources.get('timeZoneID')}
					onChange={this._changeDropdownPreference.bind(this, 'timeZoneID')}
					translateWithId={(stringId) => translateWithId(stringId)}
				/>);

				items.push(<Dropdown
					ariaLabel={StringResources.get('productLocale')}
					id="productLocale"
					selected={this.state.productLocale}
					items={makeItemsList(configSvc[configSvc.PRODUCTLOCALES])}
					itemToElement={itemToElement}
					label={configSvc[configSvc.PRODUCTLOCALES][this.state.productLocale]}
					titleText={StringResources.get('productLocale')}
					onChange={this._changeDropdownPreference.bind(this, 'productLocale')}
					translateWithId={(stringId) => translateWithId(stringId)}
				/>);

				items.push(<Dropdown
					ariaLabel={StringResources.get('contentLocale')}
					id="contentLocale"
					selected={this.state.contentLocale}
					items={makeItemsList(configSvc[configSvc.CONTENTLOCALES])}
					itemToElement={itemToElement}
					label={configSvc[configSvc.CONTENTLOCALES][this.state.contentLocale]}
					titleText={StringResources.get('contentLocale')}
					onChange={this._changeDropdownPreference.bind(this, 'contentLocale')}
					translateWithId={(stringId) => translateWithId(stringId)}
				/>);

				items.push(<SingleLineToggle
					name="bidiSupport"
					label={StringResources.get('biDirectionalFeaturesEnabled')}
					enabled={this.state.biDirectionalFeaturesEnabled}
					toggleStrings={toggleStrings}
					onToggleChange={this._changeTogglePreference.bind(this, 'biDirectionalFeaturesEnabled')}
				/>);

				if ( this.state.biDirectionalFeaturesEnabled ) {
					items.push(<Dropdown
						ariaLabel={StringResources.get('baseTextDirection')}
						id="baseTextDirection"
						items={baseTextDirections}
						itemToElement={itemToElement}
						selected={this.state.baseTextDirection}
						label={this.state.baseTextDirection}
						titleText={StringResources.get('baseTextDirection')}
						onChange={this._changeDropdownPreference.bind(this, 'baseTextDirection')}
						translateWithId={(stringId) => translateWithId(stringId)}
					/>);
				}

				// Some vertical space off the bottom so our dropdowns have room to render...
				// If Carbon ever fixes the Dropdown rendering, we can remove this...
				items.push(<div className="ddVerticalSpacer" style={{ width: '100%', height: '128px' }} />);
			}

			return items;
		}

		render() {
			return (
				<div className="settingsTab">
					{this.state.loading && <Loading
						className="loading"
						description={StringResources.get('loadingSettings')}
						withOverlay={false}
					/>}
					{this._getItems()}
				</div>
			);
		}

		_setHomeToDefault() {
			this.props.Glass.resetHome().then(() => {
				this.setState({ isHomePageDefault: true });
			});
		}

		_setAsHome() {
			const { Glass } = this.props;
			return Glass.currentAppView.setAsHome({ glassContext: Glass }).then(() => {
				Glass.perspectiveLifeCycle.openPerspectiveController.setHomeFlag(Glass.currentAppView, true);
				Glass.showToast(StringResources.get('homeSet'));
				this.setState({ isHomePageDefault: Glass.getCurrentPerspective() === 'home' });
			}, err => {
				Glass.showToast(StringResources.get('homeSetError'), { type: 'error' });
				Glass.getCoreSvc('.Logger').error(err);
			});
		}

		_toggleWelcomeBanner(showWelcomeBanner) {
			const homeUserPrefs = this._getHomeUserPrefs() || {};
			homeUserPrefs.showWelcomeBanner = showWelcomeBanner;
			window.localStorage.setItem('homeUserPrefs', JSON.stringify(homeUserPrefs));
			const eventRouter = this.props.Glass.getCoreSvc('.Events');
			eventRouter.trigger('home:toggleWelcomeBanner', showWelcomeBanner);
			this.setState({ showWelcomeBanner });
		}

		_isSetHomeEnabled() {
			return this.props.Glass.findCollection('com.ibm.bi.glass.common.setAsHome').then(collectionItems => {
				if (collectionItems) {
					const promises = [];
					collectionItems.forEach(item => {
						if (item.perspectives && item.perspectives.indexOf(this.props.Glass.getCurrentPerspective()) > -1) {
							promises.push(ClassFactory.instantiate(item.actionController));
						}
					});

					return Promise.all(promises).then((actionHandlers) => {
						return !!_.find(actionHandlers, actionHandler => {
							return actionHandler && actionHandler.isItemVisible({
								glassContext: this.props.Glass
							});
						});
					});
				} else {
					return false;
				}
			});
		}

		_getHomeUserPrefs() {
			const homeUserPrefs = window.localStorage.getItem('homeUserPrefs');
			return homeUserPrefs && JSON.parse(homeUserPrefs);
		}

		_getShowWelcomeBanner() {
			const userPref = this._getHomeUserPrefs();
			return userPref ? userPref.showWelcomeBanner : true;
		}

		_changeTogglePreference(preference, enabled) {
			this._setUserPreference(preference, enabled);
			this.setState({ [preference]: enabled });
		}

		_changeDropdownPreference(preference, evt) {
			const { id } = evt.selectedItem;
			this._setUserPreference(preference, id);
			this.setState({ [preference]: id });
		}

		_changeReportFormat(evt) {
			const restrictedFormats = this._getRestrictedFormats();
			const { id } = evt.selectedItem;
			if ( restrictedFormats[id] ) {
				this.props.Glass.showToast(StringResources.get('restrictedFormatSelected'), { type: 'info' });
			} else {
				this._changeDropdownPreference('format', evt);
			}
		}

		_getReportFormat(format) {
			const formats = this._getFormats();
			return formats[format];
		}

		_getReportFormats() {
			const formats = this._getFormats();
			return makeItemsList(formats);
		}

		_getFormats() {
			const restrictedFormats = this._getRestrictedFormats();
			const formats = {};
			formats[Constants.OUTPUT_FORMATS.HTML] = StringResources.get('html');
			formats[Constants.OUTPUT_FORMATS.PDF] = restrictedFormats[Constants.OUTPUT_FORMATS.PDF] ? StringResources.get('pdfRestricted'): StringResources.get('pdf');
			formats[Constants.OUTPUT_FORMATS.CSV] =  restrictedFormats[Constants.OUTPUT_FORMATS.CSV] ? StringResources.get('csvRestricted'): StringResources.get('csv');
			formats[Constants.OUTPUT_FORMATS.SPREADSHEETML] = restrictedFormats[Constants.OUTPUT_FORMATS.XLSXDATA] ? StringResources.get('spreadsheetMLRestricted') : StringResources.get('spreadsheetML');
			formats[Constants.OUTPUT_FORMATS.XLSXDATA] = restrictedFormats[Constants.OUTPUT_FORMATS.XLSXDATA] ? StringResources.get('xlsxDataRestricted') : StringResources.get('xlsxData');
			formats[Constants.OUTPUT_FORMATS.XML] =  restrictedFormats[Constants.OUTPUT_FORMATS.XML] ? StringResources.get('xmlRestricted'): StringResources.get('xml');
			return formats;
		}

		_getRestrictedFormats() {
			const { Glass } = this.props;
			const restrictedFormats = {};
			restrictedFormats[Constants.OUTPUT_FORMATS.PDF] = !Glass.hasCapability(Constants.CAPABILITY_NAMES.CAN_GENERATE_PDF_OUTPUT);
			restrictedFormats[Constants.OUTPUT_FORMATS.CSV] =  !Glass.hasCapability(Constants.CAPABILITY_NAMES.CAN_GENERATE_CSV_OUTPUT);
			restrictedFormats[Constants.OUTPUT_FORMATS.SPREADSHEETML] = !Glass.hasCapability(Constants.CAPABILITY_NAMES.CAN_GENERATE_XLS_OUTPUT);
			restrictedFormats[Constants.OUTPUT_FORMATS.XLSXDATA] = !Glass.hasCapability(Constants.CAPABILITY_NAMES.CAN_GENERATE_XLS_OUTPUT);
			restrictedFormats[Constants.OUTPUT_FORMATS.XML] =  !Glass.hasCapability(Constants.CAPABILITY_NAMES.CAN_GENERATE_XML_OUTPUT);
			return restrictedFormats;
		}

		_setUserPreference(key, value) {
			const userProfileSvc = this.props.Glass.getCoreSvc('.UserProfile');
			userProfileSvc.savePreferences({ [key]: value }).then(() => {
				if (key === 'showHiddenObjects') {
					const eventRouter = this.props.Glass.getCoreSvc('.Events');
					eventRouter.trigger('content:refresh');
				}
			});
		}
	};

});